/* eslint-disable react-hooks/exhaustive-deps */
import { useReducer, useMemo, useEffect, useCallback, memo } from 'react';
import UserContext from './UserContext';
import initialUserState from './initialUserState';
import userReducer from 'contexts/user/reducer/user.reducer';
import * as action from 'contexts/user/reducer/user.actions';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { TUpdateLanguageProps, TUpdateUserPick } from 'interfaces/user.interface';
import { ChildrenProps } from 'interfaces/general.interface';
import { IgetClientsAPIResponseDataItemUserItem } from 'api/endpoints/superAdmin/superAdmin.response.interface';
import useContextGeneral from 'hooks/contexts/useContextGeneral';

function UserProvider(props: ChildrenProps) {
  const [state, dispatch] = useReducer(userReducer, initialUserState);
  const { user, isAuthenticated, isLoading, getAccessTokenSilently, logout } = useAuth0();
  const { t: translate } = useTranslation();
  const { ping } = useContextGeneral();

  useEffect(() => {
    if (isLoading && !isAuthenticated) return;
    (async () => {
      if (!user?.email_verified || !ping) return;
      const token = await getAccessTokenSilently();
      if (!token) return;
      return await action.loginUserAction({
        dispatch,
        token,
        emailVerified: user.email_verified,
        translate,
        logout,
      });
    })();
  }, [user?.email_verified, isLoading, isAuthenticated, ping]);

  const login = useCallback(async () => {
    if (!user?.email_verified) return false;
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await action.loginUserAction({
      dispatch,
      token,
      emailVerified: user.email_verified,
      translate,
      logout,
    });
  }, [user?.email_verified, state.dbUser]);

  const updateUser = useCallback(
    async ({ user }: { user: TUpdateUserPick }) => {
      if (!state.dbUser?.id) return false;
      const token = await getAccessTokenSilently();
      return await action.updateUserAction({
        dispatch,
        dbUserId: state.dbUser.id,
        user,
        token,
        translate,
      });
    },
    [state.dbUser?.id]
  );

  const updateUserAvatar = useCallback(
    async ({ image }: { image: File }) => {
      if (!state.dbUser?.id) return false;
      const token = await getAccessTokenSilently();
      return await action.updateUserAvatarAction({
        dispatch,
        userId: state.dbUser.id,
        image,
        token,
        translate,
      });
    },
    [state.dbUser?.id]
  );

  const updateLanguage = useCallback(
    async ({ language }: { language: TUpdateLanguageProps }) => {
      if (!state.dbUser || language.id === state.dbUser.i18n_lang_id) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.updateLanguageAction({
        dispatch,
        dbUserId: state.dbUser.id,
        language,
        token,
        translate,
      });
    },
    [state.dbUser?.id, state.dbUser?.i18n_lang_id]
  );

  const selectUserSuperAdmin = useCallback(
    async ({ user }: { user: IgetClientsAPIResponseDataItemUserItem }) => {
      if (!user) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.selectUserSuperAdminAction({
        dispatch,
        user,
        translate,
        token,
      });
    },
    []
  );

  const memoProvider = useMemo(
    () => ({
      ...state,
      login,
      updateUser,
      updateUserAvatar,
      updateLanguage,
      selectUserSuperAdmin,
    }),
    [state, login, updateUser, updateUserAvatar, updateLanguage, selectUserSuperAdmin]
  );

  return (
    <UserContext.Provider value={memoProvider}>{props.children}</UserContext.Provider>
  );
}

export default memo(UserProvider);
